// Ring App Store: WHEP live view client.
//
// The WHEP open call follows the Ring contract verbatim through the BFF:
//   request:  Content-Type: application/sdp, body = SDP offer
//   response: 201 Created
//             Content-Type: application/sdp, body = SDP answer
//             Location: /api/cameras/{id}/live-view-session/whep/{sessionId}
//             X-Session-Ttl-Seconds: <int>
//
// `api.post` is JSON-shaped (Accept: application/json, body parsed as JSON)
// and only exposes `data` to callers — the SDP body is plain text and the
// session id + TTL live in response headers, so we drop down to fetch here.
// `src/api/**` is allowed to call fetch directly by .eslintrc.cjs.

import { api, ApiError, getCurrentRole } from "./client";
import type { WhepSessionResponse } from "@/types/streaming";

const ACTING_ROLE_HEADER = "X-Acting-Role";
const REQUEST_ID_HEADER = "X-Request-ID";
const SESSION_TTL_HEADER = "X-Session-Ttl-Seconds";
const DEFAULT_SESSION_TTL_SECONDS = 60;

export const liveViewClient = {
  /**
   * POST /api/cameras/{deviceId}/live-view-session/whep
   *
   * Body is the SDP offer (application/sdp). The BFF proxies to AVA and
   * returns the SDP answer body, the BFF-local teardown URL in the
   * Location header, and the session TTL in X-Session-Ttl-Seconds.
   */
  async openSession(
    deviceId: string,
    sdpOffer: string,
    signal?: AbortSignal,
  ): Promise<WhepSessionResponse> {
    const path = `/api/cameras/${encodeURIComponent(deviceId)}/live-view-session/whep`;

    const role = getCurrentRole();
    if (role === null || role === "") {
      throw new ApiError(0, "role_required", "X-Acting-Role required", undefined, null);
    }

    const headers = new Headers();
    headers.set("Content-Type", "application/sdp");
    headers.set("Accept", "application/sdp");
    headers.set(ACTING_ROLE_HEADER, role);

    const response = await fetch(path, {
      method: "POST",
      headers,
      body: sdpOffer,
      credentials: "same-origin",
      signal,
    });

    const requestId = response.headers.get(REQUEST_ID_HEADER) ?? undefined;

    if (!response.ok) {
      let body: unknown = null;
      let message = response.statusText;
      let code = String(response.status);
      try {
        const text = await response.text();
        if (text.length > 0) {
          try {
            body = JSON.parse(text);
            if (typeof body === "object" && body !== null) {
              const obj = body as Record<string, unknown>;
              if (typeof obj.error === "string") code = obj.error;
              if (typeof obj.message === "string") message = obj.message;
            }
          } catch {
            body = text;
            message = text;
          }
        }
      } catch {
        // ignore body parse errors — surface the status code
      }
      throw new ApiError(response.status, code, message, requestId, body);
    }

    const sdpAnswer = await response.text();
    const teardownUrl = response.headers.get("Location") ?? "";
    const ttlHeader = response.headers.get(SESSION_TTL_HEADER);
    const parsedTtl = ttlHeader !== null ? parseInt(ttlHeader, 10) : NaN;
    const sessionTtlSeconds =
      Number.isFinite(parsedTtl) && parsedTtl > 0 ? parsedTtl : DEFAULT_SESSION_TTL_SECONDS;

    // The BFF reuses the AVA session id verbatim as the trailing path
    // segment of the teardown URL, with an optional `?location=...` query
    // param appended (AVA round-trips its own location identifier here on
    // every WHEP request). Extract just the path segment so callers don't
    // mistake the query for part of the id.
    const sessionId = extractSessionIdFromTeardownUrl(teardownUrl);

    return {
      transport: "whep",
      session_id: sessionId,
      teardown_url: teardownUrl,
      sdp_answer: sdpAnswer,
      session_ttl_seconds: sessionTtlSeconds,
    };
  },

  /**
   * DELETE the teardown URL the BFF returned in the open response's Location
   * header. We use the URL verbatim because AVA echoes a `?location=...`
   * query parameter that the BFF rewrites onto its own teardown URL —
   * reconstructing from `(deviceId, sessionId)` would drop the query and
   * AVA would 400 on close.
   */
  async closeSession(teardownUrl: string): Promise<void> {
    if (!teardownUrl) return;
    await api.delete<void>(teardownUrl);
  },

  /**
   * Best-effort teardown for `beforeunload`. Uses `keepalive: true` because
   * a regular fetch is unreliable during page unload.
   *
   * NOTE: this bypasses upstream's api wrapper (and therefore the
   * X-Acting-Role header). The BFF must accept teardown without a role
   * header during unload (LLD §6.1 lists session-cleanup endpoints as
   * role-free).
   */
  closeSessionBeacon(teardownUrl: string): void {
    if (!teardownUrl) return;
    try {
      void fetch(teardownUrl, {
        method: "DELETE",
        credentials: "same-origin",
        keepalive: true,
      });
    } catch {
      // best effort
    }
  },
};

/**
 * Pull the trailing session id from a BFF teardown URL of the form
 * `/api/cameras/{deviceId}/live-view-session/whep/{sessionId}` with an
 * optional `?location=<opaque>` query string. Returns the empty string when
 * the URL doesn't match — callers fall back to retry.
 */
function extractSessionIdFromTeardownUrl(teardownUrl: string): string {
  if (!teardownUrl) return "";
  const qIdx = teardownUrl.indexOf("?");
  const pathOnly = qIdx >= 0 ? teardownUrl.substring(0, qIdx) : teardownUrl;
  const lastSlash = pathOnly.lastIndexOf("/");
  if (lastSlash < 0 || lastSlash === pathOnly.length - 1) return "";
  return pathOnly.substring(lastSlash + 1);
}
